import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BackendTypes, GetResponse } from '@tf/api';
import { GraphQLClient, gql } from '@tf/api';
import { objectUriByNodeIdQueryOptions } from '@tf/shared';
import type { ObjectRelation } from '@tf/utils';
import { createPublicSchemaId, profileRelationValue } from '@tf/utils';

import { useConfigContext } from '../hooks';

import type { JobStatus } from './jobUtils';
import { getJobRefetchInterval, isJobRunning } from './jobUtils';

export type CRACalculation = {
	id: string;
	meta: {
		'@status': JobStatus;
		'@createdAt': string;
	};
	data: {
		result?: {
			risk_level?: string;
			risk_remark?: string;
			document?: ObjectRelation<'job_attachment'>;
			template?: ObjectRelation<'job_attachment'>;
		};
	};
	attachments: {
		object: {
			id: string;
			meta: {
				'@filename': string;
				'@mime': string;
				'@createdAt': string;
				'@size': number;
			};
		};
	}[];
};

export const useCRACalculationJobs = (profileNodeId: BackendTypes.GraphId) => {
	const queryClient = useQueryClient();
	const {
		apiConfig: { clusterId },
	} = useConfigContext((s) => s);

	const allJobsQuery = useQuery({
		queryKey: ['craCalculationAll', profileNodeId],
		queryFn: async (): Promise<CRACalculation[]> => {
			const profileObjectId = await queryClient.fetchQuery(objectUriByNodeIdQueryOptions(profileNodeId));
			const res = await GraphQLClient.getInstance().request<GetResponse<CRACalculation>, { profileObjectId: string }>(
				gql`
					query ProfileCRACalculationHistory($profileObjectId: JSON!) {
						get(
							rootFilter: {
								kind: "profile_cra_calculation"
								meta: { AND: [{ key: "/@with/profile", operator: EQ, value: $profileObjectId }] }
							}
							paginationFilter: { sortByMeta: "/@createdAt", sortByDirection: DESC, limit: 50 }
						) {
							id
							meta(path: ["/@status", "/@createdAt"])
							data(path: ["/result"])
							attachments: nestedRelation(filter: { relationKind: "job_attachment" }) {
								object {
									id
									meta(path: ["/@filename", "/@mime", "/@size", "/@createdAt"])
								}
							}
						}
					}
				`,
				{ profileObjectId },
			);
			return res.get;
		},
	});

	const runningJobs = allJobsQuery.data?.filter(isJobRunning) ?? [];

	const runningJobsQueries = useQueries({
		queries: runningJobs.map((job) => {
			return queryOptions({
				queryKey: ['craCalculation', job.id],
				queryFn: async () => {
					const res = await GraphQLClient.getInstance().request<GetResponse<CRACalculation>, { jobId: string }>(
						gql`
							query ProfileCRACalculation($jobId: ID!) {
								get(rootFilter: { id: $jobId }) {
									id
									meta(path: ["/@status", "/@createdAt"])
									data(path: ["/result"])
									attachments: nestedRelation(filter: { relationKind: "job_attachment" }) {
										object {
											id
											meta(path: ["/@filename", "/@mime", "/@size", "/@createdAt"])
										}
									}
								}
							}
						`,
						{ jobId: job.id },
					);
					return res.get[0];
				},
				refetchInterval: (query) => {
					return getJobRefetchInterval(query.state.data);
				},
			});
		}),
	});

	const createJobMutation = useMutation({
		mutationFn: async (): Promise<{ id: string }> => {
			const profileObjectId = await queryClient.fetchQuery(objectUriByNodeIdQueryOptions(profileNodeId));
			const res = await GraphQLClient.getInstance().request(
				gql`
					mutation CreateProfileCRACalculation($job: ObjectInput!) {
						mutate(create: [$job]) {
							id
						}
					}
				`,
				{
					job: {
						id: 'new://job_1',
						schema: createPublicSchemaId({
							clusterId,
							schemaTag: 'types/finance/job/profile_cra_calculation',
						}),
						data: {
							payload: {
								profile: profileRelationValue(profileObjectId),
							},
						},
					},
				},
			);
			return res.mutate.find((obj: { kind: string }) => obj.kind === 'profile_cra_calculation');
		},
		onSuccess: () => {
			allJobsQuery.refetch();
		},
	});

	return {
		isLoading: allJobsQuery.isFetching || runningJobsQueries.some((q) => q.isFetching),
		isError: allJobsQuery.isError,
		data: allJobsQuery.data?.map((job) => {
			if (!isJobRunning(job)) {
				return job;
			}

			// Take fresh data from polling queries or fallback to data from initial query
			return runningJobsQueries.find((jq) => jq.data?.id === job.id)?.data ?? job;
		}),
		isCreating: createJobMutation.isPending,
		createJob: createJobMutation.mutate,
	};
};
